import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { httpService } from "../../services/httpService";
import { useProfile } from "../../context/ProfileContext";

const fetchInvoiceSnapshot = async (orderId) => {
  const res = await httpService.get(
    `/invoice/getInvoiceSnapshotByOrderId/${orderId}`,
  );
  return res?.data ?? res;
};

const formatAddress = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") return addr.trim();
  if (Array.isArray(addr))
    return addr.map(formatAddress).filter(Boolean).join(", ");

  if (typeof addr === "object") {
    if (
      typeof addr.full_address === "string" &&
      addr.full_address.trim() !== ""
    )
      return addr.full_address;
    if (typeof addr.address === "string" && addr.address.trim() !== "")
      return addr.address;
    if (addr.officeAddress || addr.homeAddress) {
      const off = formatAddress(addr.officeAddress);
      const home = formatAddress(addr.homeAddress);
      return [off, home].filter(Boolean).join(", ");
    }
    if (
      addr.buildingName ||
      addr.locality ||
      addr.city ||
      addr.state ||
      addr.pincode
    ) {
      const parts = [
        addr.buildingName,
        addr.floor,
        addr.locality,
        addr.city,
        addr.state,
        addr.pincode,
      ]
        .filter(Boolean)
        .map((s) => (typeof s === "string" ? s.trim() : s));
      return parts.join(", ");
    }
    if (typeof addr.address === "object") return formatAddress(addr.address);
    return Object.values(addr)
      .filter((v) => typeof v === "string" && v.trim() !== "")
      .join(", ");
  }
  return String(addr);
};

const cleanText = (t) => formatAddress(t);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v) => `₹${num(v).toFixed(2)}`;

const truncate = (str, n = 10) => {
  const s = String(str || "");
  return s.length > n ? s.slice(0, n) + "..." : s;
};

const fmtDate = (d) => {
  try {
    return d ? format(new Date(d), "dd/MM/yyyy") : "—";
  } catch {
    return "—";
  }
};

const fmtTime = (d) => {
  try {
    return d ? format(new Date(d), "hh:mm a") : "—";
  } catch {
    return "—";
  }
};

const numberToWords = (numVal) => {
  const value = num(numVal);
  if (!value) return "Zero Rupees Only";
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const w = (n, s) => {
    let str = n > 19 ? b[Math.floor(n / 10)] + " " + a[n % 10] : a[n];
    if (n) str += " " + s;
    return str;
  };
  const intPart = Math.floor(value);
  const decPart = Math.round((value - intPart) * 100);
  let out = "";
  out += w(Math.floor(intPart / 10000000), "Crore ");
  out += w(Math.floor((intPart / 100000) % 100), "Lakh ");
  out += w(Math.floor((intPart / 1000) % 100), "Thousand ");
  out += w(Math.floor((intPart / 100) % 10), "Hundred ");
  if (intPart > 100 && intPart % 100) out += "and ";
  out += w(Math.floor(intPart % 100), "");
  let final = "Rupees " + out.trim();
  if (decPart > 0) final += ` and ${w(decPart, "")} Paise`;
  return final + " Only";
};


const normalizeSnapshot = (snapRaw) => {
  const snap = snapRaw?.data ?? snapRaw ?? {};
  const vendor = snap.vendor ?? {};
  const billTo = snap.bill_to ?? {};
  const summary = snap.summary ?? {};

  const vendorAddress = formatAddress(vendor.address);
  const rawItems = Array.isArray(snap.items) ? snap.items : [];

  const parentMap = new Map();
  const parents = [];

  for (const it of rawItems) {
    if (it.is_child_row) continue;
    const key = String(it.ref_id ?? it._id ?? parents.length);
    const parent = {
      _id: it._id ?? key,
      key,
      service_name: it.description ?? it.service_name ?? "Item",
      item_type: it.service_type ?? "",
      sku: it.sku_number ?? "",
      batch_no: it.batch_number ?? "",
      hsn_code: it.hsn_code ?? "",
      quantity: num(it.quantity),
      mrp: num(it.mrp),
      unit_price_excl_tax: num(it.unit_price_excl_tax ?? it.mrp),
      discount: num(it.discount_amount),
      tax_percent: num(it.tax_percentage),
      tax_amount: num(it.gst_amount),
      total_price: num(it.total_amount ?? it.amount),
      is_product:
        !!it.is_product ||
        it.service_type === "product" ||
        it.service_type === "medication",
      is_boarding:
        !!it.is_boarding ||
        it.service_type === "boarding" ||
        it.service_type === "day_care",
      children: [],
    };
    parents.push(parent);
    parentMap.set(key, parent);
  }

  const isProductInvoice =
    parents.length > 0 && parents.every((p) => !!p.is_product);

  for (const it of rawItems) {
    if (!it.is_child_row) continue;
    const parentKey = String(it.parent_ref_id ?? "");
    const parent = parentMap.get(parentKey) ?? parents[parents.length - 1];
    if (!parent) continue;
    parent.children.push({
      _id: it._id ?? `${parent.key}-c${parent.children.length}`,
      service_name: it.description ?? it.service_name ?? "Item",
      sku: it.sku_number ?? "",
      batch_no: it.batch_number ?? "",
      hsn_code: it.hsn_code ?? "",
      quantity: num(it.quantity ?? 1),
      mrp: num(it.mrp),
      unit_price_excl_tax: num(it.unit_price_excl_tax ?? it.mrp),
      discount: num(it.discount_amount),
      tax_percent: num(it.tax_percentage),
      tax_amount: num(it.gst_amount),
      total_price: num(it.total_amount ?? it.amount),
    });
  }

  for (const it of rawItems) {
    if (it.is_child_row) continue;
    const key = String(it.ref_id ?? it._id ?? "");
    const parent = parentMap.get(key);
    if (!parent) continue;
    for (const c of it.children ?? []) {
      parent.children.push({
        _id: c._id ?? `${parent.key}-c${parent.children.length}`,
        service_name: c.description ?? c.name ?? "Item",
        sku: c.sku_number ?? "",
        batch_no: c.batch_number ?? "",
        hsn_code: c.hsn_code ?? "",
        quantity: num(c.quantity ?? 1),
        mrp: num(c.mrp),
        unit_price_excl_tax: num(c.unit_price_excl_tax ?? c.mrp),
        discount: num(c.discount_amount),
        tax_percent: num(c.tax_percentage),
        tax_amount: num(c.gst_amount),
        total_price: num(c.total_amount ?? c.amount),
      });
    }
  }

  const totalQty = parents.reduce((s, it) => {
    let q = it.quantity;
    it.children.forEach((c) => (q += c.quantity));
    return s + q;
  }, 0);

  const grandTotal = num(
    num(snap.amount_after_adjustment) > 0
      ? snap.amount_after_adjustment
      : snap.grand_total || snap.amount_after_adjustment || 0,
  );

  const netAmount = num(snap.net_amount);
  const itemDiscount = num(
    summary.item_discount_amount ?? snap.discount_amount ?? 0,
  );
  const calculatedSubTotal = netAmount + itemDiscount;

  const payments = (snap.payments ?? []).map((p) => {
    const rawPaid = num(p.paid_amount);
    const isPaidStatus =
      String(snap.payment_status || "").toLowerCase() === "paid";
    const resolvedAmount =
      rawPaid > 0
        ? rawPaid
        : isPaidStatus
          ? num(snap.paid_amount) > 0
            ? num(snap.paid_amount)
            : grandTotal
          : 0;
    return {
      mode: p.payment_mode ?? "—",
      amount: resolvedAmount,
      waived: num(p.waived_amount),
      date: p.payment_date ?? null,
      ref: p.reference_number ?? null,
    };
  });

  let paymentMode = "—";
  if (payments.length > 0) {
    const modes = [
      ...new Set(
        payments
          .map((p) => p.mode)
          .filter((mode) => Boolean(mode) && mode.toLowerCase() !== "unpaid"),
      ),
    ];
    if (modes.length) paymentMode = modes.join(", ");
  }

  const invoiceDate =
    snap.invoice_date ?? snap.audit?.created_at ?? new Date().toISOString();

  return {
    invoice_no: snap.invoice_no ?? "—",
    order_no: snap.order_no ?? "—",
    invoice_type: snap.invoice_type ?? "PROFORMA",
    is_product_invoice: isProductInvoice,
    invoice_mode: snap.invoice_mode ?? (snap.is_pos_order ? "pos" : "services"),
    is_pos_order: !!snap.is_pos_order,
    is_closed: !!snap.is_closed,
    header_date_label: snap.is_closed ? "Closed On" : "Date",
    invoice_date: invoiceDate,
    status: String(snap.status ?? "UNPAID").toUpperCase(),
    payment_status: String(snap.payment_status ?? "unpaid").toLowerCase(),
    vendor: {
      name: vendor.name ?? vendor.brand_name ?? "—",
      phone: vendor.phone ?? "",
      email: vendor.email ?? "",
      gstin: vendor.gstin ?? "",
      address: vendorAddress,
      logo: vendor.logo_url || vendor.logoDataUrl || vendor.logo || null,
    },
    bill_to: {
      name: billTo.name ?? "—",
      phone: billTo.phone ?? "",
    },
    items: parents,
    total_qty: totalQty,
    sub_total: num(snap.sub_total ?? calculatedSubTotal),
    net_amount: netAmount,
    item_discount: itemDiscount,
    order_discount: num(summary.order_discount_amount ?? 0),
    tax_amount: num(snap.tax_amount),
    round_off: num(snap.round_off),
    adjustment_amount: num(snap.adjustment_amount),
    grand_total: grandTotal,
    paid_amount: num(snap.paid_amount),
    waived_amount: num(snap.waived_amount),
    balance_due: num(snap.balance_due),
    payments,
    payment_mode: paymentMode,
    amount_in_words:
      snap.amount_in_words ||
      snap.invoice_amount?.amount_in_words ||
      numberToWords(grandTotal),
  };
};

const extractProfile = (
  profileData,
  fallbackVendor = {},
  localOverrides = {},
) => {
  const d = profileData || {};
  const data = d.data || d;
  const v = d.vendor || data.vendor || {};
  const profile = data.profile || d.profile || {};
  const dataAddr = data.address || d.address || {};
  const photo = data.photo || d.photo || {};

  const backendOverrides =
    data.invoiceHeaderSettings || d.invoiceHeaderSettings || {};
  const overrides = { ...backendOverrides, ...localOverrides };

  const companyName =
    overrides.name ||
    v.businessName ||
    v.brand_name ||
    v.brand ||
    v.companyName ||
    v.name ||
    profile.businessName ||
    profile.brand ||
    profile.companyName ||
    profile.name ||
    data.businessName ||
    data.brand_name ||
    data.companyName ||
    data.name ||
    fallbackVendor.businessName ||
    fallbackVendor.brand_name ||
    fallbackVendor.companyName ||
    fallbackVendor.name ||
    "";

const safeLogo = (() => {
  if (overrides.logo_url !== undefined && overrides.logo_url !== null)
    return overrides.logo_url;
  if (overrides.logo !== undefined && overrides.logo !== null)
    return overrides.logo;

  return (
    v.logo_url ||
    v.logoDataUrl ||
    v.logo ||
    data.logo_url ||
    data.logoDataUrl ||
    data.logo ||
    profile.logo_url ||
    profile.logoDataUrl ||
    profile.logo ||
    profile.companyLogo ||
    profile.companyLogoUrl ||
    photo.profilePhoto ||
    photo.logo ||
    photo.companyLogo ||
    (Array.isArray(photo.shop) && photo.shop.length > 0 ? photo.shop[0] : "") ||
    fallbackVendor.logo_url ||
    fallbackVendor.logoDataUrl ||
    fallbackVendor.logo ||
    ""
  );
})();

  return {
    name: companyName,
    signatureName:
      overrides.name || profile.ownerName || data.ownerName || companyName,
    phone:
      overrides.phone ||
      v.phone ||
      profile.phone ||
      data.phone ||
      fallbackVendor.phone ||
      "",
    email:
      overrides.email ||
      v.email ||
      profile.email ||
      data.email ||
      fallbackVendor.email ||
      "",
    gstin:
      overrides.gstin ||
      v.gstin ||
      profile.gstin ||
      data.gstin ||
      d.gstin ||
      fallbackVendor.gstin ||
      "",
    address:
      overrides.address ||
      formatAddress(v.address) ||
      formatAddress(dataAddr) ||
      formatAddress(fallbackVendor.address) ||
      "",
    logo: safeLogo,
    terms:
      overrides.terms ||
      v.terms ||
      profile.terms ||
      "Goods once sold will not be taken back or exchanged. Subject to local jurisdiction.",
  };
};

const PencilIcon = () => (
  <svg
    className="w-2.5 h-2.5"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

const EditableField = ({
  value,
  onCommit,
  placeholder = "",
  displayClassName = "",
  inputClassName = "",
  multiline = false,
  rows = 2,
  align = "left",
  isEditable = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const commit = () => {
    onCommit(draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };

  const justify =
    align === "right"
      ? "justify-end"
      : align === "center"
        ? "justify-center"
        : "justify-start";
  const textAlign =
    align === "right"
      ? "text-right"
      : align === "center"
        ? "text-center"
        : "text-left";

  const hasValue =
    value &&
    String(value).trim() !== "" &&
    value !== "—" &&
    value !== "null" &&
    value !== "[object Object]";

  if (!isEditable) {
    if (!hasValue) return null;
    return (
      <div className={`flex items-center gap-1 w-full ${justify}`}>
        <div className={`flex-1 ${textAlign} ${displayClassName}`}>
          <span className={multiline ? "whitespace-pre-wrap" : ""}>
            {value}
          </span>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div
        className={`no-print flex items-center gap-1 w-full ${justify} my-0.5`}
      >
        <button
          onClick={cancel}
          className="text-[9px] bg-slate-200 text-slate-700 px-1 py-0.5 rounded hover:bg-slate-300 font-bold shrink-0"
          title="Cancel"
        >
          X
        </button>
        {multiline ? (
          <textarea
            autoFocus
            rows={rows}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) commit();
            }}
            className={`flex-1 bg-transparent border-b border-blue-500 focus:outline-none resize-none ${textAlign} ${inputClassName}`}
            placeholder={placeholder}
          />
        ) : (
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") cancel();
            }}
            className={`flex-1 bg-transparent border-b border-blue-500 focus:outline-none ${textAlign} ${inputClassName}`}
            placeholder={placeholder}
          />
        )}
        <button
          onClick={commit}
          className="text-[9px] bg-blue-600 text-white px-1.5 py-0.5 rounded hover:bg-blue-700 font-medium shrink-0"
          title="Save"
        >
          Save
        </button>
      </div>
    );
  }

  if (!hasValue) {
    return (
      <div className={`group flex items-center gap-1 w-full ${justify} h-4`}>
        <button
          onClick={() => setEditing(true)}
          className="no-print opacity-0 group-hover:opacity-100 transition-opacity text-[9px] text-blue-500 hover:underline ml-auto"
        >
          + Add {placeholder}
        </button>
      </div>
    );
  }

  return (
    <div className={`group flex items-center gap-1 w-full ${justify}`}>
      <div className={`flex-1 ${textAlign} ${displayClassName}`}>
        <span className={multiline ? "whitespace-pre-wrap" : ""}>{value}</span>
      </div>
      <button
        onClick={() => setEditing(true)}
        className="no-print opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-blue-600 shrink-0 p-0.5 rounded"
        title={`Edit`}
      >
        <PencilIcon />
      </button>
    </div>
  );
};

const EditableLogo = ({
  value,
  onCommit,
  className = "",
  isEditable = false,
}) => {
  const fileInputRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onCommit(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onCommit("");
  };
  const hasValue =
    value &&
    String(value).trim() !== "" &&
    value !== "null" &&
    value !== "[object Object]";

  if (!isEditable) {
    if (!hasValue) return null;
    return (
      <div className={`${className}`}>
        <div className="w-full h-full flex items-center justify-center overflow-hidden">
          <img
            src={value}
            alt="Logo"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      </div>
    );
  }

  if (!hasValue) {
    return (
      <div
        className={`relative group ${className} flex items-center justify-center`}
      >
        <button
          onClick={() => fileInputRef.current?.click()}
          className="no-print opacity-0 group-hover:opacity-100 transition-opacity text-[9px] text-blue-500"
        >
          + Add Logo
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className={`relative group ${className}`}>
      <div className="w-full h-full flex items-center justify-center overflow-hidden">
        <img
          src={value}
          alt="Logo"
          className="max-w-full max-h-full object-contain"
        />
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />
      <button
        onClick={handleClear}
        className="no-print absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] shadow-sm hover:bg-red-600 z-10"
        title="Remove Logo"
      >
        ✕
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          fileInputRef.current?.click();
        }}
        className="no-print absolute -bottom-1 -right-1 bg-white border border-gray-300 p-0.5 rounded-full shadow-sm text-gray-500 hover:text-blue-600 transition"
        title="Change Logo"
      >
        <PencilIcon />
      </button>
    </div>
  );
};

const PrintStyles = () => (
  <style>{`
    @media print {
      @page { size: 80mm auto; margin: 0; }
      html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
      body * { visibility: hidden !important; }
      #invoice-print-area, #invoice-print-area * { visibility: visible !important; }
      #invoice-print-area {
        position: absolute !important;
        left: 0; top: 0;
        width: 80mm !important;
        border: none !important;
        box-shadow: none !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .no-print { display: none !important; }
    }
  `}</style>
);

export default function ThermalFormat({
  demoData = null,
  hideToolbar = false,
  isEditable = false,
}) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { profileData, fetchProfile, vendorId } = useProfile();
  const orderId =
    params.orderId ||
    searchParams.get("orderId") ||
    searchParams.get("id") ||
    "";

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Header fields
  const [profile, setProfile] = useState({
    name: "",
    phone: "",
    email: "",
    gstin: "",
    address: "",
    logo: "",
  });
  const [localOverrides, setLocalOverrides] = useState({});

  // Save UI
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const load = useCallback(async () => {
    if (demoData) {
      setInvoice(normalizeSnapshot(demoData));
      setLoading(false);
      return;
    }
    if (!orderId) {
      setError("No order id provided.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetchInvoiceSnapshot(orderId);
      setInvoice(normalizeSnapshot(res));
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load invoice snapshot.",
      );
    } finally {
      setLoading(false);
    }
  }, [orderId, demoData]);

  useEffect(() => {
    load();
  }, [load]);

  // Hydrate Profile
  useEffect(() => {
    const fallbackVendor = demoData?.vendor || {};
    const extracted = extractProfile(
      profileData,
      fallbackVendor,
      localOverrides,
    );
    setProfile(extracted);
  }, [profileData, demoData, localOverrides]);

  const handleInlineSave = async (field, value) => {
    const updatedProfile = { ...profile, [field]: value };
    setProfile(updatedProfile);

    const payload = {
      name: updatedProfile.name,
      phone: updatedProfile.phone,
      email: updatedProfile.email,
      gstin: updatedProfile.gstin,
      address: updatedProfile.address,
      logo_url: updatedProfile.logo,
      terms: updatedProfile.terms,
    };

    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);

    try {
      const endpoint = vendorId
        ? `vendorregistration/updateInvoiceHeaderSettings/${vendorId}`
        : "vendorregistration/updateInvoiceHeaderSettings";

      await httpService.put(endpoint, payload);
      setLocalOverrides((prev) => ({ ...prev, ...payload }));
      if (typeof fetchProfile === "function") await fetchProfile();

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (e) {
      setSaveError(
        e?.response?.data?.message || e?.message || "Failed to save.",
      );
      setProfile(profile);
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-600 font-mono">
        Loading invoice…
      </div>
    );
  if (error)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 gap-3">
        <p className="text-red-600 font-mono">{error}</p>
        <button
          onClick={load}
          className="px-4 py-2 bg-black text-white rounded text-sm"
        >
          Retry
        </button>
      </div>
    );
  if (!invoice) return null;

  const customerName = cleanText(invoice.bill_to.name);
  const showBatch = invoice.is_product_invoice;
  const logoBox = "w-9 h-9";

  return (
    <div
      className={
        hideToolbar
          ? "bg-white inline-block"
          : "min-h-screen bg-gray-200 font-mono"
      }
    >
      <PrintStyles />

      {/* TOOLBAR */}
      {!hideToolbar && (
        <div className="no-print sticky top-0 z-20 bg-white border-b border-gray-300 mb-6 font-sans">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div>
              <h1 className="text-sm font-semibold text-gray-800">
                Invoice Preview
              </h1>
              <p className="text-xs text-gray-500 font-mono">
                Thermal · 80mm · {invoice.invoice_no} · Order #
                {invoice.order_no || "—"}
              </p>
            </div>
            <button
              onClick={() => window.print()}
              className="px-4 py-1.5 text-sm rounded bg-black text-white hover:bg-gray-800"
            >
              🖨 Print
            </button>
          </div>
        </div>
      )}

      {/* CENTERED RECEIPT */}
      <div className={hideToolbar ? "w-full" : "flex justify-center p-6"}>
        <div id="invoice-print-area">
          <div className="bg-white px-3 py-4 w-[340px] font-mono text-[9px] leading-[13px] text-gray-900 shadow-md">
            {/* ─── HEADER (Editable) ─── */}
            <div className="flex items-start gap-1.5 mb-2">
              <EditableLogo
                value={profile.logo}
                onCommit={(val) => handleInlineSave("logo", val)}
                className={`shrink-0 ${logoBox}`}
                isEditable={isEditable}
              />

              <div className="flex-1 flex flex-col items-center text-center">
                <div className="font-bold text-[14px] tracking-widest uppercase mb-1 flex items-center justify-center gap-1">
                  {invoice.invoice_type === "TAX" ? "TAX INVOICE" : "INVOICE"}
                  {isEditable && saving && (
                    <span className="text-[8px] text-gray-500 font-normal lowercase">
                      (saving...)
                    </span>
                  )}
                  {isEditable && saveSuccess && (
                    <span className="text-[8px] text-green-600 font-normal lowercase">
                      (saved)
                    </span>
                  )}
                </div>

                <EditableField
                  value={profile.name}
                  onCommit={(val) => handleInlineSave("name", val)}
                  placeholder="Business Name"
                  align="center"
                  displayClassName="font-bold text-[11px] uppercase w-full"
                  inputClassName="text-[11px] font-bold uppercase text-center"
                  isEditable={isEditable}
                />

                <div className="w-full flex flex-col items-center gap-0.5 mt-0.5">
                  <EditableField
                    value={profile.address}
                    onCommit={(val) => handleInlineSave("address", val)}
                    placeholder="Address"
                    multiline
                    rows={2}
                    align="center"
                    displayClassName="text-[8px] text-gray-700 w-full leading-tight"
                    inputClassName="text-[8px] text-gray-700"
                    isEditable={isEditable}
                  />
                  <EditableField
                    value={profile.phone}
                    onCommit={(val) => handleInlineSave("phone", val)}
                    placeholder="Phone Number"
                    align="center"
                    displayClassName="text-[8px] text-gray-700 w-full"
                    inputClassName="text-[8px] text-gray-700"
                    isEditable={isEditable}
                  />
                  <EditableField
                    value={profile.email}
                    onCommit={(val) => handleInlineSave("email", val)}
                    placeholder="Email"
                    align="center"
                    displayClassName="text-[8px] text-gray-700 w-full"
                    inputClassName="text-[8px] text-gray-700"
                    isEditable={isEditable}
                  />
                  <EditableField
                    value={profile.gstin}
                    onCommit={(val) => handleInlineSave("gstin", val)}
                    placeholder="GSTIN"
                    align="center"
                    displayClassName="text-[8px] font-bold text-gray-800 w-full"
                    inputClassName="text-[8px] font-bold text-gray-800"
                    isEditable={isEditable}
                  />
                </div>
              </div>

              <div className={`shrink-0 ${logoBox}`} />
            </div>

            <div className="border-t border-dashed border-gray-400 my-2" />

            {/* ─── META & BILL TO ─── */}
            <div className="flex flex-col gap-0.5">
              <div className="flex justify-between">
                <span className="font-semibold">Invoice No:</span>
                <span>{invoice.invoice_no}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">Order No:</span>
                <span>{invoice.order_no}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold">
                  {invoice.header_date_label}:
                </span>
                <span>
                  {fmtDate(invoice.invoice_date)}{" "}
                  {fmtTime(invoice.invoice_date)}
                </span>
              </div>

              <div className="mt-1.5">
                <div className="font-bold">Bill To:</div>
                <div>{customerName || "—"}</div>
                {invoice.bill_to.phone && (
                  <div>Ph: {cleanText(invoice.bill_to.phone)}</div>
                )}
              </div>
            </div>

            <div className="border-t border-dashed border-gray-400 my-2" />

            {/* ─── TABLE HEADER ─── */}
            <div className="flex font-bold text-[8px] border-b border-dashed border-gray-400 pb-1 mb-1">
              <span className="w-[32%] text-left leading-tight">
                Item Name
                <br />
                SKU / HSN
                {showBatch && (
                  <>
                    <br />
                    Batch
                  </>
                )}
              </span>
              <span className="w-[16%] text-right leading-tight">
                MRP
                <br />
                Qty
              </span>
              <span className="w-[16%] text-right leading-tight">
                Disc
                <br />
                Amt
              </span>
              <span className="w-[16%] text-right leading-tight">
                Tax
                <br />
                Amt
              </span>
              <span className="w-[20%] text-right leading-tight">Total</span>
            </div>

            {/* ─── TABLE ROWS ─── */}
            {invoice.items.length === 0 ? (
              <div className="text-center text-[9px] text-gray-500 py-3">
                No line items
              </div>
            ) : (
              invoice.items.map((item, i) => (
                <React.Fragment key={item._id || i}>
                  <div className="flex items-start text-[9px] w-full border-b border-gray-100 py-1">
                    <div className="w-[32%] pr-1 min-w-0">
                      <div
                        className="font-semibold truncate w-full"
                        title={item.service_name}
                      >
                        {truncate(item.service_name, 10)}
                      </div>
                      <div className="text-[7px] text-gray-600 leading-[9px] mt-0.5 space-y-[1px]">
                        {item.sku && <div className="truncate">{item.sku}</div>}
                        {item.hsn_code && (
                          <div className="truncate">HSN: {item.hsn_code}</div>
                        )}
                        {showBatch && item.batch_no && (
                          <div className="truncate">Batch: {item.batch_no}</div>
                        )}
                      </div>
                    </div>
                    <div className="w-[16%] text-right font-bold">
                      <div>{fmtMoney(item.mrp)}</div>
                      <div className="text-[7px] text-gray-600">
                        x{item.quantity}
                      </div>
                    </div>
                    <div className="w-[16%] text-right font-bold">
                      <div>
                        {item.discount > 0 ? `${fmtMoney(item.discount)}` : "-"}
                      </div>
                      <div className="text-[7px] text-gray-600">
                        {item.discount > 0 && item.mrp * item.quantity > 0
                          ? `${Math.round((item.discount / (item.mrp * item.quantity)) * 100)}%`
                          : "-"}
                      </div>
                    </div>
                    <div className="w-[16%] text-right font-bold">
                      <div>{fmtMoney(item.tax_amount)}</div>
                      <div className="text-[7px] text-gray-600">
                        {Math.round(item.tax_percent)}%
                      </div>
                    </div>
                    <div className="w-[20%] text-right font-bold">
                      {fmtMoney(item.total_price)}
                    </div>
                  </div>

                  {item.children.map((c, ci) => (
                    <div
                      key={c._id || ci}
                      className="flex items-start text-[8px] w-full py-0.5 text-gray-600"
                    >
                      <div className="w-[32%] pr-1 pl-1 min-w-0">
                        <div className="truncate w-full" title={c.service_name}>
                          └ {truncate(c.service_name, 9)}
                        </div>
                        <div className="text-[7px] text-gray-400 space-y-[1px] mt-0.5">
                          {c.sku && <div className="truncate">{c.sku}</div>}
                          {c.hsn_code && (
                            <div className="truncate">HSN: {c.hsn_code}</div>
                          )}
                          {showBatch && c.batch_no && (
                            <div className="truncate">Batch: {c.batch_no}</div>
                          )}
                        </div>
                      </div>
                      <div className="w-[16%] text-right font-bold">
                        <div>{fmtMoney(c.mrp)}</div>
                        <div className="text-[7px] font-bold">
                          x{c.quantity}
                        </div>
                      </div>
                      <div className="w-[16%] text-right font-bold">
                        <div>
                          {c.discount > 0 ? `-${fmtMoney(c.discount)}` : "-"}
                        </div>
                        <div className="text-[7px]">
                          {c.discount > 0 && c.mrp * c.quantity > 0
                            ? `${Math.round((c.discount / (c.mrp * c.quantity)) * 100)}%`
                            : "-"}
                        </div>
                      </div>
                      <div className="w-[16%] text-right font-bold">
                        <div>{fmtMoney(c.tax_amount)}</div>
                        <div className="text-[7px]">
                          {Math.round(c.tax_percent)}%
                        </div>
                      </div>
                      <div className="w-[20%] text-right font-bold">
                        {fmtMoney(c.total_price)}
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ))
            )}

            <div className="border-t border-dashed border-gray-400 my-2" />

            {/* ─── TOTALS ─── */}
            <div className="flex justify-between items-start">
              <div className="w-[40%] font-semibold">
                Total Qty:{" "}
                <span className="font-bold text-[11px]">
                  {invoice.total_qty}
                </span>
              </div>

              <div className="w-[60%] flex flex-col gap-0.5">
                {invoice.item_discount > 0 && (
                  <>
                    <div className="flex justify-between">
                      <span>Sub Total</span>
                      <span>{fmtMoney(invoice.sub_total)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Item Discount</span>
                      <span>- {fmtMoney(invoice.item_discount)}</span>
                    </div>
                  </>
                )}

                {invoice.order_discount > 0 && (
                  <div className="flex justify-between">
                    <span>Order Discount</span>
                    <span>-{fmtMoney(invoice.order_discount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold">
                  <span>Net Amount</span>
                  <span>{fmtMoney(invoice.net_amount)}</span>
                </div>
                <div className="flex justify-between font-bold">
                  <span>Tax</span>
                  <span>{fmtMoney(invoice.tax_amount)}</span>
                </div>
                {invoice.round_off !== 0 && (
                  <div className="flex justify-between">
                    <span>Round Off</span>
                    <span>
                      {invoice.round_off > 0 ? "+" : "-"}
                      {fmtMoney(Math.abs(invoice.round_off))}
                    </span>
                  </div>
                )}
                {invoice.adjustment_amount > 0 && (
                  <div className="flex justify-between">
                    <span>Adjustment</span>
                    <span>-{fmtMoney(invoice.adjustment_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-[10px] mt-1">
                  <span>Order Amount</span>
                  <span>
                    <span>₹{Math.round(Number(invoice.grand_total))}</span>{" "}
                  </span>
                </div>
                <div className="flex justify-between mt-1">
                  <span>Payment Status</span>
                  <span className="uppercase font-semibold">
                    {invoice.payment_status === "paid"
                      ? "PAID"
                      : invoice.payment_status === "partial"
                        ? "PARTIAL"
                        : "UNPAID"}
                  </span>
                </div>
                {invoice.balance_due !== 0 && (
                  <div className="flex justify-between font-bold text-[10px]">
                    <span>Balance Due</span>
                    <span>{fmtMoney(invoice.balance_due)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* ─── PAYMENTS ─── */}
            {(() => {
              const paidPayments = invoice.payments.filter(
                (p) => p.mode && p.mode.toLowerCase() !== "unpaid",
              );
              if (!paidPayments.length) return null;
              return (
                <>
                  <div className="border-t border-dashed border-gray-400 my-2" />
                  <div className="font-bold text-[8px] mb-0.5">Payments</div>
                  {paidPayments.map((p, i) => (
                    <div key={i} className="flex justify-between text-[8px]">
                      <span className="capitalize">
                        {p.mode}
                        {p.date ? ` · ${fmtDate(p.date)}` : ""}
                        {p.ref ? ` · ${p.ref}` : ""}
                      </span>
                      <span className="font-bold">{fmtMoney(p.amount)}</span>
                    </div>
                  ))}
                </>
              );
            })()}

            <div className="border-t border-dashed border-gray-400 my-2" />

            {/* ─── WORDS & PAYMENT MODE ─── */}
            <div className="text-left leading-tight">
              <div className="text-[8px]">
                <span className="font-semibold">Total Amount in words:</span>
                <div className="italic text-gray-700 mt-0.5">
                  {invoice.amount_in_words}
                </div>
              </div>
              <div className="text-[8px] mt-1.5">
                <span className="font-semibold">Payment Mode:</span>{" "}
                <span className="uppercase font-bold">
                  {invoice.payment_mode}
                </span>
              </div>
            </div>

            <div className="border-t border-dashed border-gray-400 my-2" />
          </div>
        </div>
      </div>
    </div>
  );
}
